import { NewExchangeArticle } from "@/components/article-item/new-exchange-article";
import ArticlesExchanged from "@/components/articles-lists/articles-exchanged";
import ArticlesNotReturned from "@/components/articles-lists/articles-not-returned";
import ArticlesNotTreatedByLogistician from "@/components/articles-lists/articles-not-treated-by-logistician";
import ArticlesRejectedByLogistician from "@/components/articles-lists/articles-rejected-by-logistician";
import ArticlesToHandleByMerchant from "@/components/articles-lists/articles-to-handle-by-merchant";
import ShopLocationSelect from "@/components/shop-location-select/shop-location-select";
import useViewerRole from "@/lib/rights/use-viewer-role";
import { useBoolean } from "@/hooks/use-boolean";
import BillingDetails from "@/pages/(app)/returns/refund/components/billing-details";
import {
  ReturnRequestExchangeNewProductForExchangeCreditDto,
  ReturnRequestLineItemDto,
} from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Accordion } from "@babackapp/ui/components/accordion";
import { Button } from "@babackapp/ui/components/button";
import { useCurrentReturnRequest } from "../utils/current-return-request-context";
import { useCanValidate } from "./hooks/use-can-validate";
import { useRefundLineItems } from "./hooks/use-refund-line-items-context";
import { useRefundContext } from "./use-refund-context";
import ReturnCheckSingleBoxField from "@/pages/(app)/returns/refund/components/return-check-single-box-field";
import { useRefundLineItemsContainARefund } from "@/pages/(app)/returns/refund/hooks/use-refund-line-items-contain-a-refund.ts";
import ArticlesValidatedByLogistician from "@/components/articles-lists/articles-validated-by-logistician.tsx";
import { useRefundLineItemsContainADiscount } from "./hooks/use-refund-line-items-contain-a-discount";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";

export function ReturnRefundForm({
  onSubmit,
}: {
  onSubmit: (
    lineItem: ReturnRequestLineItemDto[],
    returnRequestId: string,
    data: object,
  ) => Promise<void> | void;
  handleClose: () => void;
}) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const currentReturnRequest = useCurrentReturnRequest();
  const role = useViewerRole();
  const isLogistician = role === "logistician";

  const {
    doNotChargeForRefund,
    doNotRefundInitialShipping,
    forceRefundInitialShipping,
    setDoNotChargeForRefund,
    setDoNotRefundInitialShipping,
    setForceRefundInitialShipping,
    restockLocation,
    setRestockLocation,
    bypassStock: byPassStock,
    setBypassStock: setByPassStock,
  } = useRefundContext();

  const { items: lineItems } = useRefundLineItems();
  const containARefund = useRefundLineItemsContainARefund();
  const containADiscount = useRefundLineItemsContainADiscount();
  const canValidate = useCanValidate();
  const submitting = useBoolean(false);

  const includeExchangeItems =
    lineItems.filter(
      (item) =>
        item.resolution === "exchange" || item.resolution === "exchange-credit",
    ).length > 0;

  const handleSubmit = async () => {
    if (submitting.value) return;
    submitting.setTrue();
    try {
      await onSubmit(lineItems, currentReturnRequest.id, {});
    } finally {
      submitting.setFalse();
    }
  };

  return (
    <div className="no-scrollbar overflow-auto">
      <div className="space-y-8 flex-1">
        <div className="space-y-4">
          <div className="my-2 space-y-6">
            <div>
              <Accordion
                type="multiple"
                defaultValue={
                  isLogistician
                    ? ["notTreatedByLogistician"]
                    : ["toHandleByMerchant", "validatedByLogistician"]
                }
              >
                <ArticlesToHandleByMerchant />
                <ArticlesValidatedByLogistician />
                <ArticlesRejectedByLogistician />
                <ArticlesNotTreatedByLogistician />
                <ArticlesNotReturned />
                <ArticlesExchanged />
              </Accordion>
            </div>
            {!isLogistician && includeExchangeItems && (
              <ReturnCheckSingleBoxField
                label={t("returnDetail.confirmModal.byPassStock")}
                onChange={setByPassStock}
                value={byPassStock}
              />
            )}
          </div>
        </div>

        {role !== "logistician" && (
          <>
            <ShopLocationSelect
              value={restockLocation}
              onChange={setRestockLocation}
            />
            {!currentReturnRequest.financial_informations.return_shipping_paid
              .done &&
              ((shop?.discount?.applyReturnShippingFeesForDiscountRule &&
                containADiscount) ||
                containARefund) && (
                <ReturnCheckSingleBoxField
                  label={t("returnDetail.confirmModal.doNotChargeForRefund")}
                  onChange={() =>
                    setDoNotChargeForRefund(!doNotChargeForRefund)
                  }
                  value={!doNotChargeForRefund}
                />
              )}
            {!currentReturnRequest.financial_informations
              .initial_shipping_refunded.done &&
            currentReturnRequest.financial_informations
              .initial_shipping_refunded.presentment_amount &&
            (containADiscount || containARefund) ? (
              <ReturnCheckSingleBoxField
                label={t(
                  "returnDetail.confirmModal.doNotRefundInitialShipping",
                )}
                onChange={() =>
                  setDoNotRefundInitialShipping(!doNotRefundInitialShipping)
                }
                value={doNotRefundInitialShipping}
              />
            ) : null}
            {!currentReturnRequest.financial_informations
              .initial_shipping_refunded.done &&
            currentReturnRequest.financial_informations
              .initial_shipping_refunded.sourceAmount &&
            !currentReturnRequest.financial_informations
              .initial_shipping_refunded.presentment_amount &&
            (containADiscount || containARefund) ? (
              <ReturnCheckSingleBoxField
                label={t(
                  "returnDetail.confirmModal.forceRefundInitialShipping",
                )}
                onChange={() =>
                  setForceRefundInitialShipping(!forceRefundInitialShipping)
                }
                value={forceRefundInitialShipping}
              />
            ) : null}
            <BillingDetails />
          </>
        )}
      </div>
      <div className="flex items-center justify-center gap-4 sticky bottom-0 py-8 mt-10 bg-gradient-to-b from-white/0 to-white">
        <Button
          variant="default"
          disabled={!canValidate || submitting.value}
          onClick={handleSubmit}
        >
          {t("misc.validate")}
        </Button>
      </div>
    </div>
  );
}

interface NewProductForExchangeCreditListProps {
  items: ReturnRequestExchangeNewProductForExchangeCreditDto[];
  currency: string;
}

export const NewProductForExchangeCreditList = ({
  items,
  currency,
}: NewProductForExchangeCreditListProps) => {
  return (
    <div className="border-border border rounded-md flex-col gap-4 divide-y">
      {items.map((item) => (
        <NewExchangeArticle key={item.id} article={item} currency={currency} />
      ))}
    </div>
  );
};
